// hooks/useCommandContext.ts
/**
 * Single source of the CommandContext handed to `executeCommand`.
 *
 * Both command surfaces — the chat/StudentCommandBar path
 * (`useCommandExecution`) and the admin CommandPalette — used to build this
 * object themselves, each ending in an `as unknown as CommandContext` cast,
 * and they had already drifted (the palette wired `onClearSemester` to the
 * real bulk operation while the chat path re-implemented it as a reverse
 * `removeCourse` loop). One builder, one wiring.
 */

import { useMemo } from 'react';
import type { CommandContext } from '../components/CommandPalette/utils/commandExecutor';
import { useInstitutionConfig, useInstitutionTheme } from '../contexts/InstitutionConfigContext';
import type { AcademicPlan } from '../types';
import type { PlanOperations } from './useAcademicPlan';
import type { CourseOps, CourseSearchResult } from './useCourseData';

/**
 * Subset of plan operations required for command execution. Pick from the
 * canonical PlanOperations so signatures (incl. course-input shape) stay
 * consistent across the codebase.
 */
export type CommandPlanOps = Pick<
  PlanOperations,
  | 'addCourse'
  | 'removeCourse'
  | 'moveCourse'
  | 'addToBookmarks'
  | 'removeFromBookmarks'
  | 'createPlaceholder'
  | 'clearAllCourses'
  | 'clearSemester'
  | 'applyTemplate'
>;

/**
 * Subset of course operations required for command execution.
 */
export type CommandCourseOps = Pick<CourseOps, 'searchCourses'>;

export interface UseCommandContextParams {
  academicPlan: AcademicPlan;
  planOps: CommandPlanOps;
  courses: CourseSearchResult[];
  courseOps: CommandCourseOps;
}

/**
 * Build the context object `executeCommand` consumes, including the
 * per-institution flags its handlers read (grade-data gating for INFO,
 * example course codes for HELP).
 */
export function useCommandContext({
  academicPlan,
  planOps,
  courses,
  courseOps,
}: UseCommandContextParams): CommandContext {
  const { config: institutionConfig } = useInstitutionConfig();
  const { exampleCourseCodes } = useInstitutionTheme();

  return useMemo<CommandContext>(() => ({
    academicPlan,
    onAddCourse: planOps.addCourse,
    onRemoveCourse: planOps.removeCourse,
    onMoveCourse: planOps.moveCourse,
    onAddToBookmarks: planOps.addToBookmarks,
    onRemoveFromBookmarks: planOps.removeFromBookmarks,
    onCreatePlaceholder: planOps.createPlaceholder,
    // The real bulk operation: it strips non-placeholder courses from the
    // semester in one backend round per course and refreshes the audit.
    onClearSemester: planOps.clearSemester,
    onClearAll: planOps.clearAllCourses,
    onApplyTemplate: planOps.applyTemplate,
    courses,
    onSearchCourses: courseOps.searchCourses,
    hasGradeData: institutionConfig.hasGradeData,
    exampleCourseCodes,
  }), [
    academicPlan,
    planOps,
    courses,
    courseOps,
    institutionConfig.hasGradeData,
    exampleCourseCodes,
  ]);
}
